/**
 * FHIR Connector configuration component.
 * Specialized configuration for FHIR connector (SMART on FHIR / Standard OAuth2).
 */

// React Imports
import { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  FormControlLabel,
  Switch,
  TextField,
} from '@mui/material';
import { OAuthConnectButton } from './OAuthConnectButton';

export interface FhirConnectorConfigProps {
  instanceId?: string;
  settingsJson?: string;
  isConnected?: boolean;
  onChange?: (settingsJson: string) => void;
  onConnected?: () => void;
}

/**
 * FHIR Connector configuration component.
 */
export const FhirConnectorConfig = ({
  instanceId,
  settingsJson = '{}',
  isConnected = false,
  onChange,
  onConnected,
}: FhirConnectorConfigProps) => {
  const [serverType, setServerType] = useState<'smart' | 'standard'>('smart');
  const [baseUrl, setBaseUrl] = useState('');
  const [fhirVersion, setFhirVersion] = useState('R4');
  const [hipaaAcknowledged, setHipaaAcknowledged] = useState(false);

  const handleServerTypeChange = (newType: 'smart' | 'standard') => {
    setServerType(newType);
    const settings = JSON.parse(settingsJson || '{}');
    settings.serverType = newType;
    onChange?.(JSON.stringify(settings));
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" sx={{ mb: 3 }}>
          FHIR Server Configuration
        </Typography>

        <Box sx={{ mb: 3 }}>
          <TextField
            label="FHIR Server Base URL"
            value={baseUrl}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setBaseUrl(e.target.value);
              const settings = JSON.parse(settingsJson || '{}');
              settings.baseUrl = e.target.value;
              onChange?.(JSON.stringify(settings));
            }}
            fullWidth
            required
            helperText="Base URL of the FHIR server (e.g., https://fhir.epic.com/interconnect-fhir-oauth/api/FHIR/R4)"
            sx={{ mb: 2 }}
          />
          {/* FHIR Version Select */}
          <TextField
            label="FHIR Version"
            value={fhirVersion}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setFhirVersion(e.target.value);
              const settings = JSON.parse(settingsJson || '{}');
              settings.fhirVersion = e.target.value;
              onChange?.(JSON.stringify(settings));
            }}
            fullWidth
            select
            SelectProps={{ native: true }}
          >
            <option value="R4">R4</option>
            <option value="STU3">STU3</option>
            <option value="DSTU2">DSTU2</option>
          </TextField>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Server Type
          </Typography>
          <ToggleButtonGroup
            value={serverType}
            exclusive
            onChange={(_, newType) => newType && handleServerTypeChange(newType)}
            fullWidth
          >
            <ToggleButton value="smart">SMART on FHIR</ToggleButton>
            <ToggleButton value="standard">Standard OAuth2</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {serverType === 'smart' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {instanceId && (
              <OAuthConnectButton
                instanceId={instanceId}
                connectorName="FHIR Server"
                isConnected={isConnected}
                onConnected={onConnected}
              />
            )}
            <Alert severity="info">
              SMART on FHIR requires OAuth 2.0 with healthcare-specific scopes (e.g., patient/*.read, user/*.read).
            </Alert>
          </Box>
        )}

        {serverType === 'standard' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {instanceId && (
              <OAuthConnectButton
                instanceId={instanceId}
                connectorName="FHIR Server"
                isConnected={isConnected}
                onConnected={onConnected}
              />
            )}
            <Alert severity="info">
              Standard OAuth2 authentication for generic FHIR servers.
            </Alert>
          </Box>
        )}

        <Box sx={{ mt: 3, pt: 3, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Compliance & Settings
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={hipaaAcknowledged}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setHipaaAcknowledged(e.target.checked);
                  const settings = JSON.parse(settingsJson || '{}');
                  settings.hipaaAcknowledged = e.target.checked;
                  onChange?.(JSON.stringify(settings));
                }}
              />
            }
            label={
              <Box>
                <Typography variant="body2">HIPAA Compliance Acknowledgment</Typography>
                <Typography variant="caption" color="text.secondary">
                  I acknowledge that this connector will access protected health information (PHI) and will comply with HIPAA requirements.
                </Typography>
              </Box>
            }
          />
        </Box>
      </CardContent>
    </Card>
  );
};
